<template>
  <div class="home">
    <h2>Achieved goals</h2>
    <!-- Filtering -->
    <b-row>
      <b-col md="12" class="my-1">
        <b-form-group label-cols-sm="1" label="Filter" class="mb-0">
          <b-input-group>
            <b-form-input
              v-model="filter.userId"
              placeholder="by userId"
              @keypress.enter="forceFilter"
            />
            <b-form-input
              v-model="filter.goalName"
              placeholder="by goal name"
              @keypress.enter="forceFilter"
            />
            <b-form-input
              v-model="filter.appInstallId"
              placeholder="by appInstallId"
              @keypress.enter="forceFilter"
            />

            <b-input-group-append>
              <b-button
                :disabled="
                  !filter.appInstallId && !filter.login && !filter.email
                "
                @click="
                  filter = {};
                  forceFilter();
                "
                >Clear</b-button
              >
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-container fluid>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @filtered="onFiltered"
        ref="achievedGoalsTable"
        class="achievedGoalsTable"
      >
        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            @click="row.toggleDetails"
            variant="info"
          >
            …
          </b-button>

          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            @click="
              deleteGoal({
                target: $event.target,
                goal: row.item
              })
            "
          >
            X
          </b-button>
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [5, 20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: {
        goalName: '',
        userId: '',
        appInstallId: ''
      }
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'appInstallId',
          label: 'appInstallId',
          sortable: false,
          class: 'text-center'
        },
        {
          key: 'userId',
          label: 'user ID',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'goalName',
          label: 'Goal name',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'goalDateTime',
          label: 'goalDateTime',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'goalParams',
          label: 'goalParams',
          sortable: false,
          class: 'text-center'
        },
        {
          key: 'abtestingId',
          label: 'abtestingId',
          sortable: false,
          class: 'text-center'
        },
        {
          key: 'stuckingCount',
          label: 'stuckingCount',
          sortable: false,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.achievedGoals.count;
    }
  },
  methods: {
    forceFilter() {
      this.$refs.achievedGoalsTable.refresh();
    },
    deleteGoal({ goal = null }) {
      if (!goal) {
        return;
      }
      const promise = this.$store.dispatch('deleteAchievedGoal', goal.id);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.achievedGoalsTable.refresh();
      });
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    items(ctx) {
      const currentFilter = Object.keys(this.filter).reduce(
        (acc, curr) =>
          this.filter[curr] ? { ...acc, [curr]: this.filter[curr] } : acc,
        {}
      );

      const promise = this.$store.dispatch('getAchievedGoals', {
        ctx,
        filter: currentFilter
      });

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        return this.$store.state.achievedGoals.rows || [];
      });
    }
  },
  mounted() {
    this.$store.dispatch('getVersions');
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.achievedGoalsTable {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
